import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import PollCard from "@/components/base/PollCard";
import { useSeo } from "@/hooks/useSeo";
import { topics } from "@/mocks/topics";
import { formatDate, formatNumber } from "@/utils/format";
import TopicArticle from "@/pages/topics/detail/components/TopicArticle";
import TopicAside from "@/pages/topics/detail/components/TopicAside";
import TopicComments from "@/pages/topics/detail/components/TopicComments";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((item) => item.id === id);

  const relatedPool = topic
    ? topics.filter((item) => item.id !== topic.id && item.categorySlug === topic.categorySlug)
    : [];
  const relatedFallback = topic
    ? topics.filter((item) => item.id !== topic.id && item.categorySlug !== topic.categorySlug)
    : [];
  const related = [...relatedPool, ...relatedFallback].slice(0, 4);

  useSeo({
    title: topic ? `${topic.title} - 爆料看点` : "话题不存在 - 爆料看点",
    description: topic
      ? topic.summary
      : "该话题可能已被合并或调整，请返回话题广场浏览正在讨论的内容。",
    keywords: topic
      ? `${topic.tags.join(",")},爆料看点,评论区高能回复,网友神评论,投票结果展示`
      : "爆料看点,话题广场",
    path: topic ? `/topics/${topic.id}` : "/topics",
    jsonLd: topic
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.title,
            description: topic.summary,
            image: topic.cover,
            datePublished: topic.date,
            dateModified: topic.date,
            inLanguage: "zh-CN",
            articleSection: topic.category,
            keywords: topic.tags.join(","),
            author: {
              "@type": "Organization",
              name: "爆料看点",
            },
            publisher: {
              "@type": "Organization",
              name: "爆料看点",
            },
            mainEntityOfPage: `https://qrwho.cn/topics/${topic.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://qrwho.cn/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "话题广场",
                item: "https://qrwho.cn/topics",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: topic.category,
                item: `https://qrwho.cn/topics?category=${topic.categorySlug}`,
              },
              {
                "@type": "ListItem",
                position: 4,
                name: topic.title,
                item: `https://qrwho.cn/topics/${topic.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-xl flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-3xl" aria-hidden="true" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            没有找到这个话题
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            话题可能已经合并或调整了编号。返回话题广场，可以继续浏览正在被讨论的内容。
          </p>
          <Link
            to="/topics"
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
          >
            返回话题广场
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-10">
        <Breadcrumbs
          items={[
            { label: "话题广场", to: "/topics" },
            { label: topic.category, to: `/topics?category=${topic.categorySlug}` },
            { label: topic.title },
          ]}
        />

        <header className="max-w-4xl">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/topics?category=${topic.categorySlug}`}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-100 px-3 py-1 font-label text-xs text-primary-800 transition-colors hover:bg-primary-200"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-price-tag-3-line" aria-hidden="true" />
              </span>
              {topic.category}
            </Link>
            {topic.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-background-200 px-3 py-1 font-label text-xs text-foreground-600"
              >
                #{tag}
              </span>
            ))}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {topic.title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {topic.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                <i className="ri-edit-line" aria-hidden="true" />
              </span>
              {topic.author}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                <i className="ri-calendar-line" aria-hidden="true" />
              </span>
              {formatDate(topic.date)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                <i className="ri-fire-line" aria-hidden="true" />
              </span>
              {formatNumber(topic.heat)} 热度
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                <i className="ri-chat-3-line" aria-hidden="true" />
              </span>
              {formatNumber(topic.commentCount)} 条讨论
            </span>
            <span className="rounded-full bg-background-100 px-2.5 py-1">{topic.readTime}</span>
          </div>
        </header>

        <div className="mt-6 h-[240px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
          <img
            src={topic.cover}
            alt={`${topic.title} 话题封面`}
            title={`${topic.title} - 爆料看点`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-9 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="space-y-8 lg:col-span-8">
            <TopicArticle topic={topic} />
            <PollCard poll={topic.poll} topicTitle={topic.title} />
            <TopicComments topic={topic} />
          </div>
          <div className="lg:col-span-4">
            <TopicAside topic={topic} related={related} />
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}